import { z } from 'zod';
import { INDUSTRY_KEYS } from '../industries';
import { addressSchema, optionalEmail, optionalPhone, optionalText } from './common';
import { bpSchema } from '../money';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');

export const generalSettingsSchema = z.object({
  name: z.string().trim().min(2).max(120),
  industry: z.enum(INDUSTRY_KEYS),
  email: optionalEmail(),
  phone: optionalPhone(),
  website: optionalText(200),
  address: addressSchema.optional(),
  timezone: z.string().trim().min(1).max(60),
  currency: z.string().trim().length(3).toUpperCase(),
  dateFormat: z.enum(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']),
  locale: z.string().trim().min(2).max(20),
  fiscalYearStartMonth: z.coerce.number().int().min(1).max(12),
});
export type GeneralSettingsInput = z.input<typeof generalSettingsSchema>;

export const workingHoursSchema = z.object({
  workingDays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  start: hhmm,
  end: hhmm,
  lateGraceMinutes: z.coerce.number().int().min(0).max(180),
});

export const sessionPolicySchema = z.object({
  enforceIdleLogout: z.boolean(),
  idleTimeoutMinutes: z.coerce.number().int().min(1).max(240),
  idleWarningSeconds: z.coerce.number().int().min(10).max(600),
  maxConcurrentSessions: z.coerce.number().int().min(0).max(20),
  absoluteSessionHours: z.coerce.number().int().min(1).max(24 * 30),
});
export type SessionPolicyInput = z.input<typeof sessionPolicySchema>;

export const passwordPolicySchema = z.object({
  minLength: z.coerce.number().int().min(10).max(64),
  maxFailedAttempts: z.coerce.number().int().min(3).max(20),
  lockoutMinutes: z.coerce.number().int().min(1).max(1440),
});

const termSchema = z.object({ singular: z.string().trim().min(1).max(40), plural: z.string().trim().min(1).max(40) });

export const terminologySchema = z.object({
  client: termSchema,
  lead: termSchema,
  employee: termSchema,
  task: termSchema,
});

export const leadStageConfigSchema = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{0,39}$/, 'Lowercase letters, digits and underscores'),
  label: z.string().trim().min(1).max(40),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  order: z.coerce.number().int().min(0).max(100),
  isWon: z.boolean().optional(),
  isLost: z.boolean().optional(),
});

export const crmSettingsSchema = z
  .object({
    leadStages: z.array(leadStageConfigSchema).min(2).max(20),
    leadSources: z
      .array(
        z.object({
          key: z
            .string()
            .trim()
            .regex(/^[a-z][a-z0-9_]{0,39}$/),
          label: z.string().trim().min(1).max(40),
        }),
      )
      .min(1)
      .max(50),
    leadAssignment: z.enum(['manual', 'round_robin']),
    roundRobinUserIds: z.array(z.string().regex(/^[a-f\d]{24}$/i)).max(100),
  })
  .refine((v) => v.leadStages.filter((s) => s.isWon).length === 1, { message: 'Exactly one stage must be marked Won' })
  .refine((v) => v.leadStages.filter((s) => s.isLost).length === 1, { message: 'Exactly one stage must be marked Lost' })
  .refine((v) => new Set(v.leadStages.map((s) => s.key)).size === v.leadStages.length, {
    message: 'Stage keys must be unique',
  });
export type CrmSettingsInput = z.input<typeof crmSettingsSchema>;

export const heartbeatSchema = z.object({
  /** Milliseconds since the last user interaction observed by the client. */
  idleForMs: z.number().int().min(0).max(7 * 24 * 3600 * 1000),
  tabVisible: z.boolean(),
});
export type HeartbeatInput = z.output<typeof heartbeatSchema>;

export const breakStartSchema = z.object({
  reason: optionalText(200),
});

// ------------------------------------------------------------------ finance & sales (2026-11)
/**
 * What counts towards a revenue target. Always the employee's attributed share (after splits),
 * so shared deals are never double counted.
 * - accepted: share of the final agreed contract value, in the month the project was converted (default)
 * - invoiced: share of amounts invoiced to the client in the month
 * - collected: share of money actually received in the month (net of refunds)
 */
export const TARGET_BASES = ['accepted', 'invoiced', 'collected'] as const;
export type TargetBasis = (typeof TARGET_BASES)[number];
export const TARGET_BASIS_LABELS: Record<TargetBasis, string> = {
  accepted: 'Accepted project value (counted at conversion)',
  invoiced: 'Invoiced sales value (counted when billed)',
  collected: 'Collected revenue (counted when received)',
};
export const COMMISSION_REVERSAL_POLICIES = ['reverse_unpaid', 'recover_from_payroll'] as const;
export type CommissionReversalPolicy = (typeof COMMISSION_REVERSAL_POLICIES)[number];

export const financeSettingsSchema = z.object({
  /** Default GST / tax rate for new quotation lines, in basis points (0 = none configured). */
  defaultTaxRateBp: bpSchema,
  quotationValidityDays: z.coerce.number().int().min(1).max(365),
  /** When on, a quotation must be approved before it can be sent or accepted. */
  quotationApprovalRequired: z.boolean(),
  invoiceDueDays: z.coerce.number().int().min(0).max(365),
  companyGstin: optionalText(20),
});
export type FinanceSettingsInput = z.input<typeof financeSettingsSchema>;

export const salesSettingsSchema = z.object({
  /** What counts towards revenue targets. */
  targetBasis: z.enum(TARGET_BASES),
  /** Allow total attributed credit on a project to exceed its eligible value (normally off). */
  allowOverAttribution: z.boolean(),
  /** What happens to approved commissions when a project is cancelled or refunded. */
  commissionReversalPolicy: z.enum(COMMISSION_REVERSAL_POLICIES),
});
export type SalesSettingsInput = z.input<typeof salesSettingsSchema>;

export interface FinanceSettings {
  defaultTaxRateBp: number;
  quotationValidityDays: number;
  quotationApprovalRequired: boolean;
  invoiceDueDays: number;
  companyGstin?: string | null;
}
export interface SalesSettings {
  targetBasis: TargetBasis;
  allowOverAttribution: boolean;
  commissionReversalPolicy: CommissionReversalPolicy;
}
export const DEFAULT_FINANCE_SETTINGS: FinanceSettings = {
  defaultTaxRateBp: 0,
  quotationValidityDays: 30,
  quotationApprovalRequired: false,
  invoiceDueDays: 15,
  companyGstin: null,
};
export const DEFAULT_SALES_SETTINGS: SalesSettings = {
  targetBasis: 'accepted',
  allowOverAttribution: false,
  commissionReversalPolicy: 'reverse_unpaid',
};
